<template>
  <div class="automation-builder">
    <div class="automation-builder__content">
      <VueFlow
        v-model="elements"
        @update:model-value="onElementsChange"
        :default-edge-type="SmoothStepEdge"
        :default-edges="{ type: 'smoothstep' }"
        :default-zoom="1.5"
        :min-zoom="0.2"
        :max-zoom="4"
        :auto-connect="true"
        class="automation-builder__flow"
        @nodesDragged="onNodesDragged"
        @nodesChanged="onNodesChanged"
        @edgesChanged="onEdgesChanged"
        @nodeClick="onNodeClick"
      >
        <template #node-trigger="nodeProps">
          <TriggerNode
            v-bind="nodeProps"
            @add-node="addAction"
            :active="activeNode?.id === nodeProps.id"
            :id="nodeProps.id"
          />
        </template>
        <template #node-action="nodeProps">
          <ActionNode
            v-bind="nodeProps"
            @add-node="addAction"
            :currentAutomation="automation"
            :active="activeNode?.id === nodeProps.id"
            :id="nodeProps.id"
          />
        </template>
        <template #node-condition="nodeProps">
          <ConditionNode
            v-bind="nodeProps"
            @add-node="addAction"
            :currentAutomation="automation"
            :active="activeNode?.id === nodeProps.id"
            :id="nodeProps.id"
          />
        </template>

        <Controls />
        <Background variant="dots" :gap="18" :size="1" pattern-color="#cbd5e1" />
      </VueFlow>
    </div>
    <Drawer
      v-if="activeNode"
      :show="!!activeNode"
      :props="{
        icon:
          activeNode?.type === 'trigger'
            ? 'zap'
            : activeNode?.type === 'condition'
              ? 'split'
              : 'bullseye',
        subtitle:
          activeNode?.type === 'trigger'
            ? 'Trigger'
            : activeNode?.type === 'condition'
              ? 'Condition'
              : 'Action',
        title: getNodeTitle(activeNode),
        description: activeNodeRunError || 'Configure the settings for this block',
      }"
      @close="closeNodeSettings"
      @refresh="refreshNode"
      @delete="deleteNode"
    >
      <NodeSettings
        :type="activeNode?.type"
        :nodeData="activeNode?.data"
        :hasConnections="hasNodeConnections(activeNode?.id)"
        :currentAutomation="automation"
        @update="updateNodeData"
      />
      <template #footer v-if="activeNode.type === 'action' || activeNode.type === 'condition'">
        <Btn variant="danger" @click="deleteNode">Delete node</Btn>
      </template>
    </Drawer>
  </div>
</template>

<script>
  import { ref, watch, nextTick, provide, computed } from 'vue';
  import { VueFlow, useVueFlow, SmoothStepEdge } from '@vue-flow/core';
  import { Background } from '@vue-flow/background';
  import { Controls } from '@vue-flow/controls';
  import '@vue-flow/core/dist/style.css';
  import '@vue-flow/core/dist/theme-default.css';
  import TriggerNode from './nodes/TriggerNode.vue';
  import ActionNode from './nodes/ActionNode.vue';
  import ConditionNode from './nodes/ConditionNode.vue';
  import { debounce } from 'lodash-es';
  import Drawer from '@/components/drawer/Drawer.vue';
  import { TRIGGER_TYPES, ACTION_TYPES, enrichTriggerForDisplay, normalizeTriggerForSave } from '@/constants/automations';
  import NodeSettings from './NodeSettings.vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'AutomationBuilder',

    components: {
      VueFlow,
      Background,
      Controls,
      TriggerNode,
      ActionNode,
      ConditionNode,
      Drawer,
      NodeSettings,
      Btn,
      SmoothStepEdge,
    },

    props: {
      automation: {
        type: Object,
        required: true,
      },
      runStatus: {
        type: String,
        default: null,
      },
      runSteps: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['update', 'save'],

    setup(props, { emit }) {
      const { addNodes, addEdges, getOutgoers } = useVueFlow();
      const elements = ref([]);
      const isInitializing = ref(true);
      const activeNode = ref(null);
      const skipElementsEcho = ref(false);

      const runStepMap = computed(() => {
        const map = {};
        (props.runSteps || []).forEach((step) => {
          if (step?.step_id) map[step.step_id] = step;
        });
        return map;
      });
      provide('automationRunStepMap', runStepMap);
      provide(
        'automationRunStatus',
        computed(() => props.runStatus)
      );

      const setElements = (next) => {
        skipElementsEcho.value = true;
        elements.value = next;
        nextTick(() => {
          skipElementsEcho.value = false;
        });
      };

      const withoutDraftIntegrationRows = (data) => {
        if (!data || data.type !== 'integration' || !data.config) return data;
        const config = data.config;
        return {
          ...data,
          config: {
            ...config,
            eventProperties: Array.isArray(config.eventProperties)
              ? config.eventProperties.filter((p) => p?.key)
              : config.eventProperties,
            mailingLists: Array.isArray(config.mailingLists)
              ? config.mailingLists.filter((m) => m?.listId)
              : config.mailingLists,
            contactFields: Array.isArray(config.contactFields)
              ? config.contactFields.filter((f) => f?.acFieldId)
              : config.contactFields,
          },
        };
      };

      const nodeDataSignature = (els) =>
        JSON.stringify(
          (els || [])
            .filter((el) => el && !('source' in el))
            .map((el) => ({
              id: el.id,
              type: el.type,
              data: withoutDraftIntegrationRows(el.data),
            }))
            .sort((a, b) => String(a.id).localeCompare(String(b.id)))
        );

      function getTriggerConfig(type, details) {
        switch (type) {
          case 'form_submission':
            return { formId: details?.formId || null, formDetails: details?.formDetails || null };
          case 'lead_status_change':
            return {
              from_status: details?.from_status || null,
              to_status: details?.to_status || null,
            };
          case 'deal_created':
            return {
              pipelineId: details?.pipelineId || null,
              source: details?.source || 'any',
            };
          case 'deal_status_change': {
            const emptyOrAnyStage = (v) => {
              if (v == null || v === '') return null;
              const s = String(v).toLowerCase();
              return s === 'any stage' || s === 'null' ? null : v;
            };
            const from = emptyOrAnyStage(details?.fromStageId ?? details?.from_stage_id);
            const to = emptyOrAnyStage(details?.toStageId ?? details?.to_stage_id);
            return {
              pipelineId: details?.pipelineId || null,
              fromStageId: from === null ? '' : from,
              toStageId: to === null ? '' : to,
              statusType: details?.statusType ?? details?.status_type ?? null,
            };
          }
          case 'proposal_accepted':
          case 'proposal_rejected':
            return { proposalType: details?.proposalType || 'any' };
          case 'proposal_status_change':
            return {
              fromStatus: details?.fromStatus || 'any',
              toStatus: details?.toStatus || 'any',
              proposalType: details?.proposalType || 'any',
            };
          case 'project_status_change':
          case 'project_fully_completed':
            return {
              fromStatus: details?.fromStatus || 'any',
              toStatus:
                details?.toStatus ||
                (type === 'project_fully_completed' ? 'completed' : 'any'),
            };
          case 'work_order_completed':
          case 'work_visit_confirmed':
          case 'work_visit_completed':
          case 'project_all_work_orders_completed':
            return { workOrderType: details?.workOrderType || 'any' };
          case 'scheduled':
            return {
              frequency: details?.frequency || 'daily',
              schedule_settings:
                details?.schedule_settings ||
                {
                  daily: { time: '09:00' },
                  weekly: { day: 1, time: '09:00' },
                  monthly: { day: 1, time: '09:00' },
                  custom: { cron: '0 9 * * *' },
                }[details?.frequency || 'daily'],
            };
          default:
            return {};
        }
      }

      function normalizeConditionDetails(details) {
        if (!details) return { field: '', operator: 'equals', value: '' };
        // Legacy list form: { conditions: [...] }
        if (Array.isArray(details.conditions) && details.conditions.length) {
          const c = details.conditions[0];
          return {
            field: c.field || '',
            operator: c.operator || 'equals',
            value: c.value ?? '',
          };
        }
        if (details.condition && typeof details.condition === 'object') {
          return {
            field: details.condition.field || '',
            operator: details.condition.operator || 'equals',
            value: details.condition.value ?? '',
          };
        }
        if (details.field || details.operator) {
          return {
            field: details.field || '',
            operator: details.operator || 'equals',
            value: details.value ?? '',
          };
        }
        return { field: '', operator: 'equals', value: '' };
      }

      function convertToElements(automation) {
        if (!automation) {
          return [
            {
              id: 'trigger-node',
              type: 'trigger',
              position: { x: 290, y: 50 },
              data: {
                type: 'form_submission',
                config: { formId: null },
              },
            },
          ];
        }

        const elements = [];

        // Add trigger node
        const displayTriggerType = enrichTriggerForDisplay(
          automation.trigger_type,
          automation.trigger_details || {}
        );
        elements.push({
          id: 'trigger-node',
          type: 'trigger',
          position: { x: 290, y: 50 },
          data: {
            type: displayTriggerType,
            config: getTriggerConfig(displayTriggerType, automation.trigger_details),
          },
        });

        const steps = automation.steps || [];

        // Collect branch-target step IDs referenced by any condition.
        const branchTargets = new Map(); // childStepId -> { parentId, branch }
        steps.forEach((step) => {
          if (step.action_type !== 'condition') return;
          const d = step.action_details || {};
          if (d.true_step) {
            branchTargets.set(d.true_step, { parentId: step.id, branch: 'true' });
          }
          if (d.false_step) {
            branchTargets.set(d.false_step, { parentId: step.id, branch: 'false' });
          }
        });

        // First pass: build nodes
        steps.forEach((step, index) => {
          const backendType = step.action_type || step.type || 'task';
          let nodeType = backendType;
          let nodeConfig = step.action_details || step.configuration || {};

          if (backendType === 'condition') {
            nodeType = 'condition';
            const d = step.action_details || {};
            nodeConfig = {
              condition: normalizeConditionDetails(d),
              true_step: d.true_step || null,
              false_step: d.false_step || null,
            };
          } else {
            // Backward compat: old task+fire_integration → integration
            if (
              nodeType === 'task' &&
              (nodeConfig.taskType === 'fire_integration' ||
                step.configuration?.taskType === 'fire_integration')
            ) {
              const cfg = step.configuration || nodeConfig;
              nodeType = 'integration';
              nodeConfig = { integrationId: cfg.integrationId || null };
            }

            if (nodeType === 'integration') {
              nodeConfig = {
                integrationId: nodeConfig.integration_id || nodeConfig.integrationId || null,
                tag: nodeConfig.tag ?? '',
                contactFields: Array.isArray(nodeConfig.contactFields)
                  ? nodeConfig.contactFields
                  : Array.isArray(nodeConfig.contact_fields)
                    ? nodeConfig.contact_fields.map((f) => ({
                        acFieldId: f.ac_field_id ?? f.acFieldId ?? '',
                        value: f.value ?? '',
                      }))
                    : [],
                eventName: nodeConfig.event_name ?? nodeConfig.eventName ?? '',
                eventProperties: Array.isArray(nodeConfig.eventProperties)
                  ? nodeConfig.eventProperties
                  : Array.isArray(nodeConfig.event_properties)
                    ? nodeConfig.event_properties.map((p) => ({
                        key: p.key ?? '',
                        value: p.value ?? '',
                      }))
                    : [],
                mailingLists: Array.isArray(nodeConfig.mailingLists)
                  ? nodeConfig.mailingLists
                  : Array.isArray(nodeConfig.mailing_lists)
                    ? nodeConfig.mailing_lists.map((m) => ({
                        listId: m.list_id ?? m.listId ?? '',
                        action:
                          m.action === 'remove' || m.subscribed === false
                            ? 'remove'
                            : 'add',
                      }))
                    : [],
              };
            } else if (nodeType === 'task') {
              nodeConfig = {
                taskType: nodeConfig.task_type || nodeConfig.taskType || null,
                celebrationMessage:
                  nodeConfig.celebration_message || nodeConfig.celebrationMessage || '',
                proposalConfig:
                  nodeConfig.proposal_config || nodeConfig.proposalConfig || null,
              };
            } else if (nodeType === 'move_deal_stage') {
              nodeConfig = {
                pipelineId: nodeConfig.pipeline_id || nodeConfig.pipelineId || null,
                toStageId:
                  nodeConfig.to_stage_id || nodeConfig.toStageId || null,
              };
            } else if (nodeType === 'assign_user') {
              const rawUserIds =
                nodeConfig.user_ids ?? nodeConfig.userIds ?? [];
              nodeConfig = {
                assignmentMode:
                  nodeConfig.assignment_mode || nodeConfig.assignmentMode || 'specific',
                userIds: Array.isArray(rawUserIds) ? rawUserIds.filter(Boolean) : [],
              };
            } else if (nodeType === 'create_deal_task') {
              const rawDays = nodeConfig.due_in_days ?? nodeConfig.dueInDays ?? 0;
              const parsedDays = parseInt(rawDays, 10);
              nodeConfig = {
                title: nodeConfig.title ?? '',
                description: nodeConfig.description ?? '',
                taskType: nodeConfig.task_type || nodeConfig.taskType || 'general',
                priority: nodeConfig.priority || 'medium',
                dueInDays:
                  Number.isInteger(parsedDays) && parsedDays >= 0 ? parsedDays : 0,
                assignTo: nodeConfig.assign_to || nodeConfig.assignTo || 'deal_owner',
              };
            } else if (nodeType === 'delay') {
              // Accept the canonical { duration, unit } shape and the legacy
              // { delay: { value, unit } } / { duration_value, duration_unit }
              // shapes the backend documents in its v1 contract.
              const legacyDelay =
                nodeConfig.delay && typeof nodeConfig.delay === 'object'
                  ? nodeConfig.delay
                  : null;
              const rawDuration =
                nodeConfig.duration ??
                legacyDelay?.value ??
                nodeConfig.duration_value ??
                1;
              const parsedDuration = parseInt(rawDuration, 10);
              nodeConfig = {
                duration:
                  Number.isInteger(parsedDuration) && parsedDuration > 0
                    ? parsedDuration
                    : 1,
                unit:
                  nodeConfig.unit ||
                  legacyDelay?.unit ||
                  nodeConfig.duration_unit ||
                  'hours',
              };
            }
          }

          const branchInfo = branchTargets.get(step.id);
          const previousStepId = branchInfo
            ? branchInfo.parentId
            : step.previous_step_id ||
              (index === 0 ? 'trigger-node' : steps[index - 1].id);

          elements.push({
            id: step.id,
            type: nodeType === 'condition' ? 'condition' : 'action',
            position: { x: NODE_START_X, y: 0 },
            data: {
              type: nodeType,
              config: nodeConfig,
              previousSteps: [previousStepId],
              branchKey: branchInfo ? branchInfo.branch : null,
            },
          });
        });

        // Second pass: build edges. Branch-targets get an edge from the
        // condition with the correct sourceHandle; everything else uses
        // the regular previous_step_id chain.
        steps.forEach((step, index) => {
          const branchInfo = branchTargets.get(step.id);
          if (branchInfo) {
            elements.push({
              id: `e-${branchInfo.parentId}-${step.id}-${branchInfo.branch}`,
              source: branchInfo.parentId,
              sourceHandle: branchInfo.branch,
              target: step.id,
              type: 'smoothstep',
              style: { strokeWidth: 2 },
            });
          } else {
            const sourceId =
              step.previous_step_id ||
              (index === 0 ? 'trigger-node' : steps[index - 1].id);
            elements.push({
              id: `e-${sourceId}-${step.id}`,
              source: sourceId,
              target: step.id,
              type: 'smoothstep',
              style: { strokeWidth: 2 },
            });
          }
        });

        return elements;
      }

      const lastEmittedElements = ref(null);

      function normalizeStageId(v) {
        if (v == null || v === '') return null;
        const s = String(v).toLowerCase();
        return s === 'any stage' || s === 'null' ? null : v;
      }

      function convertToAutomation(elements) {
        const trigger = elements.find((el) => el.type === 'trigger');
        const steps = elements.filter(
          (el) => el.type === 'action' || el.type === 'condition'
        );
        const edges = elements.filter((el) => 'source' in el);

        // Preserve existing automation steps data
        const existingSteps = props.automation?.steps || [];

        const triggerTypeRaw = trigger?.data?.type || 'form_submission';
        let triggerDetails = trigger?.data?.config || {};

        if (triggerTypeRaw === 'deal_status_change') {
          triggerDetails = {
            ...triggerDetails,
            fromStageId: normalizeStageId(triggerDetails.fromStageId),
            toStageId: normalizeStageId(triggerDetails.toStageId),
          };
        }

        const { triggerType, triggerDetails: normalizedDetails } = normalizeTriggerForSave(
          triggerTypeRaw,
          triggerDetails
        );
        triggerDetails = normalizedDetails;

        return {
          ...props.automation,
          trigger_type: triggerType,
          trigger_details: triggerDetails,
          form_id: triggerType === 'form_submission' ? triggerDetails.formId || null : null,
          steps: steps.map((step) => {
            const existingStep = existingSteps.find((es) => es.id === step.id);
            const config = step.data.config || {};
            const actionType = step.data.type;
            let actionDetails = {};

            if (actionType === 'condition') {
              const outgoing = edges.filter((e) => e.source === step.id);
              const trueEdge = outgoing.find((e) => e.sourceHandle === 'true');
              const falseEdge = outgoing.find((e) => e.sourceHandle === 'false');
              const cond = config.condition || {
                field: '',
                operator: 'equals',
                value: '',
              };
              actionDetails = {
                condition: {
                  field: cond.field || '',
                  operator: cond.operator || 'equals',
                  value: cond.value ?? '',
                },
                true_step: trueEdge?.target || null,
                false_step: falseEdge?.target || null,
              };
            } else if (actionType === 'integration') {
              actionDetails = {
                integration_id: config.integrationId || null,
                tag: config.tag || null,
                contactFields: config.contactFields || [],
                event_name: config.eventName || null,
                // Keep empty draft rows while editing; they are stripped on save.
                event_properties: Array.isArray(config.eventProperties)
                  ? config.eventProperties.map((p) => ({
                      key: p?.key ?? '',
                      value: p?.value ?? '',
                    }))
                  : [],
                mailing_lists: Array.isArray(config.mailingLists)
                  ? config.mailingLists.map((m) => ({
                      list_id: m?.listId || '',
                      subscribed: m?.action !== 'remove',
                    }))
                  : [],
              };
            } else if (actionType === 'task') {
              actionDetails = {
                task_type: config.taskType || null,
                celebration_message: config.celebrationMessage || null,
                proposal_config: config.proposalConfig || null,
              };
            } else if (actionType === 'move_deal_stage') {
              actionDetails = {
                pipeline_id: config.pipelineId || null,
                to_stage_id: config.toStageId || null,
              };
            } else if (actionType === 'assign_user') {
              const mode = config.assignmentMode === 'random' ? 'random' : 'specific';
              const userIds = Array.isArray(config.userIds)
                ? config.userIds.filter(Boolean)
                : [];
              actionDetails = {
                assignment_mode: mode,
                // 'specific' wijst altijd één persoon toe; bij random is dit de pool.
                user_ids: mode === 'specific' ? userIds.slice(0, 1) : userIds,
              };
            } else if (actionType === 'create_deal_task') {
              const parsedDays = parseInt(config.dueInDays, 10);
              actionDetails = {
                title: config.title || '',
                description: config.description || null,
                task_type: config.taskType || 'general',
                priority: config.priority || 'medium',
                due_in_days:
                  Number.isInteger(parsedDays) && parsedDays >= 0 ? parsedDays : 0,
                assign_to: config.assignTo || 'deal_owner',
              };
            } else if (actionType === 'delay') {
              const parsedDuration = parseInt(config.duration, 10);
              actionDetails = {
                duration:
                  Number.isInteger(parsedDuration) && parsedDuration > 0
                    ? parsedDuration
                    : 1,
                unit: config.unit || 'hours',
              };
            } else {
              actionDetails = config;
            }

            return {
              id: step.id,
              action_type: actionType,
              action_details: actionDetails,
              configuration: {
                ...(existingStep?.configuration && typeof existingStep.configuration === 'object'
                  ? existingStep.configuration
                  : {}),
                ...config,
              },
              previous_step_id: step.data.previousSteps?.[0] || null,
            };
          }),
        };
      }

      const NODE_VERTICAL_SPACING = 180;
      const NODE_HORIZONTAL_SPACING = 200;
      const NODE_START_X = 290;

      function arrangeNodes() {
        if (!elements.value.length) return;

        const triggerNode = elements.value.find((el) => el.type === 'trigger');
        if (!triggerNode) return;

        triggerNode.position = { x: NODE_START_X, y: 50 };

        const edges = elements.value.filter((el) => 'source' in el);
        const nodeById = new Map(
          elements.value
            .filter((el) => !('source' in el))
            .map((el) => [el.id, el])
        );
        const visited = new Set();

        function positionSubtree(nodeId, x, y) {
          if (visited.has(nodeId)) return;
          visited.add(nodeId);

          const node = nodeById.get(nodeId);
          if (!node) return;
          node.position = { x, y };

          const outgoing = edges.filter((e) => e.source === nodeId);
          if (!outgoing.length) return;

          if (node.type === 'condition') {
            const trueEdge = outgoing.find((e) => e.sourceHandle === 'true');
            const falseEdge = outgoing.find((e) => e.sourceHandle === 'false');
            const childY = y + NODE_VERTICAL_SPACING;
            if (trueEdge) {
              positionSubtree(trueEdge.target, x - NODE_HORIZONTAL_SPACING, childY);
            }
            if (falseEdge) {
              positionSubtree(falseEdge.target, x + NODE_HORIZONTAL_SPACING, childY);
            }
            return;
          }

          outgoing.forEach((edge) => {
            positionSubtree(edge.target, x, y + NODE_VERTICAL_SPACING);
          });
        }

        positionSubtree(triggerNode.id, NODE_START_X, 50);

        elements.value = [...elements.value];
      }

      const onElementsChange = (newElements) => {
        if (isInitializing.value) {
          isInitializing.value = false;
          return;
        }
        if (skipElementsEcho.value) {
          return;
        }
        elements.value = newElements;
        arrangeNodes();
        emitUpdate(newElements, 'onElementsChange');
      };

      const addAction = (action) => {
        const isCondition = action.action === 'condition';
        const newNodeId = `${isCondition ? 'condition' : 'action'}-${Date.now()}`;

        let defaultConfig = {};
        if (isCondition) {
          defaultConfig = {
            condition: { field: '', operator: 'equals', value: '' },
            true_step: null,
            false_step: null,
          };
        } else if (action.action === 'move_deal_stage') {
          // Pre-fill pipelineId from the trigger if it has one set, so the
          // stage dropdown is ready to use immediately.
          const triggerPipelineId =
            props.automation?.trigger_details?.pipelineId || null;
          defaultConfig = {
            pipelineId: triggerPipelineId,
            toStageId: null,
          };
        } else if (action.action === 'delay') {
          defaultConfig = { duration: 1, unit: 'hours' };
        } else if (action.action === 'assign_user') {
          defaultConfig = { assignmentMode: 'specific', userIds: [] };
        } else if (action.action === 'create_deal_task') {
          defaultConfig = {
            title: '',
            description: '',
            taskType: 'general',
            priority: 'medium',
            dueInDays: 0,
            assignTo: 'deal_owner',
          };
        }

        const newNode = {
          id: newNodeId,
          type: isCondition ? 'condition' : 'action',
          position: { x: NODE_START_X, y: 0 },
          data: {
            type: action.action,
            config: defaultConfig,
            previousSteps: [action.afterId],
            branchKey: action.sourceHandle || null,
          },
        };

        addNodes(newNode);

        const previousNode = elements.value.find((el) => el.id === action.afterId);
        if (previousNode) {
          const newEdge = {
            id: action.sourceHandle
              ? `e${previousNode.id}-${newNodeId}-${action.sourceHandle}`
              : `e${previousNode.id}-${newNodeId}`,
            source: previousNode.id,
            target: newNodeId,
            ...(action.sourceHandle ? { sourceHandle: action.sourceHandle } : {}),
          };
          addEdges(newEdge);
        }

        const newElements = [...elements.value, newNode];
        elements.value = newElements;
        arrangeNodes();
        emitUpdate(newElements, 'addAction');
      };

      // Initialize elements when component mounts
      elements.value = convertToElements(props.automation);
      arrangeNodes();

      // Watch for automation prop changes
      watch(
        () => props.automation,
        (newVal) => {
          if (isInitializing.value || !newVal) return;

          const newElements = convertToElements(newVal);
          // convertToElements always resets positions, which would otherwise
          // look like a change and bounce the graph (and input values) back.
          if (nodeDataSignature(newElements) === nodeDataSignature(elements.value)) {
            return;
          }

          const activeId = activeNode.value?.id;
          setElements(newElements);
          if (activeId) {
            activeNode.value =
              newElements.find((el) => el.id === activeId) || activeNode.value;
          }
          arrangeNodes();
        },
        { deep: true }
      );

      const getNodeTitle = (node) => {
        if (node?.type === 'trigger') {
          return TRIGGER_TYPES.find((t) => t.value === node.data.type)?.label || node.data.type;
        }
        if (node?.type === 'condition') {
          return 'Condition (If / Else)';
        }
        return ACTION_TYPES.find((a) => a.value === node.data.type)?.label || node.data.type;
      };

      const hasNodeConnections = (nodeId) => {
        const node = elements.value.find((el) => el.id === nodeId);
        if (!node) return false;
        return getOutgoers({ id: nodeId, data: node.data }).length > 0;
      };

      const closeNodeSettings = () => {
        activeNode.value = null;
      };

      const refreshNode = () => {
        // Implement refresh logic
      };

      const collectDescendantIds = (rootId) => {
        const ids = new Set();
        const edges = elements.value.filter((el) => 'source' in el);
        const queue = [rootId];
        while (queue.length) {
          const current = queue.shift();
          edges
            .filter((e) => e.source === current)
            .forEach((e) => {
              if (!ids.has(e.target)) {
                ids.add(e.target);
                queue.push(e.target);
              }
            });
        }
        return ids;
      };

      const deleteNode = () => {
        if (!activeNode.value) return;

        const rootId = activeNode.value.id;
        // When a condition (or any branching node) is removed we also
        // remove its descendants so no orphans remain.
        const toRemove = new Set([rootId]);
        if (activeNode.value.type === 'condition') {
          collectDescendantIds(rootId).forEach((id) => toRemove.add(id));
        }

        elements.value = elements.value.filter((el) => {
          if (toRemove.has(el.id)) return false;
          if (
            'source' in el &&
            (toRemove.has(el.source) || toRemove.has(el.target))
          ) {
            return false;
          }
          return true;
        });

        emitUpdate(elements.value, 'deleteNode');
        closeNodeSettings();
      };

      const updateNodeData = (newData, nodeId) => {
        const targetNodeId = nodeId || activeNode.value?.id;

        if (!targetNodeId) return;

        const nextElements = elements.value.map((el) => {
          if (el.id === targetNodeId) {
            return {
              ...el,
              data: {
                ...el.data,
                type: newData.type,
                config: newData.config || {},
                previousSteps: newData.previousSteps || el.data.previousSteps,
              },
            };
          }
          return el;
        });

        setElements(nextElements);

        emitUpdate(nextElements, 'updateNodeData');
      };

      const emitUpdate = debounce((elements, origin) => {
        // Skip if elements haven't changed
        if (
          lastEmittedElements.value &&
          JSON.stringify(elements) === JSON.stringify(lastEmittedElements.value)
        ) {
          return;
        }

        // Store current elements for future comparison
        lastEmittedElements.value = JSON.parse(JSON.stringify(elements));

        const newAutomation = convertToAutomation(elements);
        emit('update', newAutomation);
      }, 300);
      const onNodeClick = ({ node }) => {
        activeNode.value = node;
      };

      const activeNodeRunError = computed(() => {
        if (!activeNode.value) return '';
        if (activeNode.value.type === 'trigger') return '';
        return runStepMap.value[activeNode.value.id]?.error_message || '';
      });

      return {
        elements,
        onElementsChange,
        onNodesDragged: () => {},
        onNodesChanged: () => {},
        onEdgesChanged: () => {},
        addAction,
        activeNode,
        activeNodeRunError,
        getNodeTitle,
        hasNodeConnections,
        closeNodeSettings,
        refreshNode,
        deleteNode,
        updateNodeData,
        onNodeClick,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .automation-builder {
    background: transparent;
    display: flex;
    flex-direction: row;
    height: 100%;
    flex: 1;
    min-width: 0;

    &__content {
      flex: 1;
      position: relative;
      min-width: 0;
    }

    &__flow {
      height: 100%;
    }
  }

  :deep(.vue-flow) {
    background: #f8fafc;
  }
  :deep(.vue-flow__panel) {
    background: #ffffff;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    box-shadow: none;
  }
  :deep(.vue-flow__edge-path) {
    stroke: var(--slate-300);
    stroke-width: 1.5;
  }
</style>
